import React, { useEffect, useRef, useState } from 'react';
import { EyeOffIcon, SendIcon, SparklesIcon } from 'lucide-react';
import { useAssessment, useInstrumentConfig } from '../../contexts/AssessmentContext';
function renderContent(content: string) {
  return content.split('\n').map((paragraph, i) => {
    if (paragraph.trim() === '') return <div key={i} className="h-2" data-id="element-115" data-mp="32d99i43wcw87" />;
    const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
    return <p key={i} className="text-[13.5px] leading-relaxed" data-id="element-116" data-mp="2enevvkbr73mw" data-mpi={i}>
        {parts.map((part, j) => part.startsWith('**') && part.endsWith('**') ? <strong key={j} className="font-semibold" data-id="element-117" data-mp="2ocezxi544p7y">
              {part.slice(2, -2)}
            </strong> : <React.Fragment key={j} data-id="element-118" data-mp="2jlgclylpiqv7">{part}</React.Fragment>)}
      </p>;
  });
}
export function AIChat() {
  const {
    messages,
    sendPrompt,
    recordPaste,
    thinking,
    promptCount,
    locked,
    timeUp,
    sharedDocs
  } = useAssessment();
  const config = useInstrumentConfig();
  const [input, setInput] = useState('');
  const [chipUsed, setChipUsed] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const readOnly = locked || timeUp;
  useEffect(() => {
    endRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, thinking]);
  const submit = (e: React.FormEvent | React.KeyboardEvent) => {
    e.preventDefault();
    if (!input.trim() || thinking || readOnly) return;
    sendPrompt(input, chipUsed !== null && chipUsed === input.trim());
    setInput('');
    setChipUsed(null);
  };
  return <section className="flex h-full min-h-0 flex-col bg-white" aria-label={config.assistant.name} data-id="element-119" data-mp="2zobtaj6qdgpr">
      <header className="flex items-center gap-3 border-b border-slate-200 px-5 py-3" data-id="element-120" data-mp="34nf0a969tx0d">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-900" data-id="element-121" data-mp="38o8nt1fsx9sq">
          <SparklesIcon className="h-4 w-4 text-teal-400" data-id="element-122" data-mp="jujxwlcl7he2" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-123" data-mp="nvnamyzc8xb2">
          <h2 className="text-sm font-semibold text-slate-900" data-id="element-124" data-mp="nyrlgf7b6rap">{config.assistant.name}</h2>
          <p className="truncate text-xs text-slate-500" data-id="element-125" data-mp="2kvhssm9m8dko">{config.assistant.subtitle}</p>
        </div>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono-num text-[11px] font-semibold text-slate-600" data-id="element-126" data-mp="2g0c5pk4h8sv0">
          {promptCount} prompts
        </span>
      </header>

      <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-5 py-2.5" data-id="element-127" data-mp="2ozfqqtn7fic5">
        <EyeOffIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" data-id="element-128" data-mp="219v4o1vr84yn" />
        <p className="text-[12px] leading-snug text-amber-900" data-id="element-129" data-mp="1x8rpzipg0ez0">
          This assistant{' '}
          {config.assistant.shareable ? <>{config.assistant.notice}</> : <>
              <strong className="font-semibold" data-id="element-130" data-mp="267myj1fym15h">cannot see</strong> {config.assistant.notice}
            </>}
        </p>
      </div>

      {config.assistant.shareable && <div className="border-b border-slate-200 bg-slate-50 px-5 py-2" data-id="element-131" data-mp="1dkivh20ffwwc">
          <p className="font-mono-num text-[11px] text-slate-500" data-id="element-132" data-mp="3p7co4q7ugkig">
            {sharedDocs.length === 0 ? 'Shared with assistant: nothing yet' : `Shared with assistant: ${sharedDocs.join(' · ')}`}
          </p>
        </div>}

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5" data-id="element-133" data-mp="3g895fncla74h">
        {messages.length === 0 && <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4" data-id="element-134" data-mp="3l34z0rxh52jp">
            <p className="text-[13px] font-medium text-slate-700" data-id="element-135" data-mp="cnl6xw8jhq68">Start with a question.</p>
            <p className="mt-1 text-xs text-slate-500" data-id="element-136" data-mp="gqhum1cxsch1">{config.assistant.emptyHint}</p>
            <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-137" data-mp="2r01j9slyd1v">
              {config.assistant.chips.map(s => <button key={s} type="button" onClick={() => {
            setInput(s);
            setChipUsed(s);
          }} className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11.5px] text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900" data-id="element-138" data-mp="7spgv1mv5cvr">
                  {s}
                </button>)}
            </div>
          </div>}

        {messages.map(m => m.role === 'user' ? <div key={m.id} className="flex justify-end" data-id="element-139" data-mp="2u0deourm0mrw">
              <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-slate-900 px-4 py-2.5 text-[13.5px] leading-relaxed text-white" data-id="element-140" data-mp="3qp2ywrqplrh1">
                {m.content}
              </div>
            </div> : <div key={m.id} className="flex justify-start" data-id="element-141" data-mp="3hnd7hlwtusj9">
              <div className="max-w-[92%] space-y-1 rounded-2xl rounded-bl-sm border border-slate-200 bg-slate-50 px-4 py-3 text-slate-800" data-id="element-142" data-mp="3cptsukw7wdqs">
                {renderContent(m.content)}
              </div>
            </div>)}

        {thinking && <div className="flex items-center gap-1.5 px-1 text-slate-400" aria-live="polite" data-id="element-143" data-mp="21chma292ph0x">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" data-id="element-144" data-mp="1r8axz0waef4x" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" data-id="element-145" data-mp="1w3gl231fdzul" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" data-id="element-146" data-mp="212t38ahcqiqu" />
          </div>}
        <div ref={endRef} data-id="element-147" data-mp="29wzfrdzh44ch" />
      </div>

      <form onSubmit={submit} className="border-t border-slate-200 bg-white p-3" data-id="element-148" data-mp="29vxuyhsno3hk">
        <div className="flex items-end gap-2 rounded-lg border border-slate-300 bg-white p-2 focus-within:border-slate-500 focus-within:ring-2 focus-within:ring-slate-200" data-id="element-149" data-mp="2ev9cvu4rdf0q">
          <label htmlFor="assistant-input" className="sr-only" data-id="element-150" data-mp="2ou88ab0ije5s">
            Message the {config.assistant.name}
          </label>
          <textarea id="assistant-input" value={input} onChange={e => setInput(e.target.value)} onPaste={e => {
          const text = e.clipboardData.getData('text');
          if (text.trim()) recordPaste(text);
        }} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit(e);
          }
        }} rows={2} disabled={readOnly} placeholder={config.assistant.placeholder} className="max-h-40 flex-1 resize-none bg-transparent px-1.5 py-1 text-[13.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:opacity-50" data-id="element-151" data-mp="phw0dhmhskoj" />
          <button type="submit" disabled={!input.trim() || thinking || readOnly} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-900 text-white transition-colors hover:bg-slate-700 disabled:bg-slate-300" aria-label="Send message" data-id="element-152" data-mp="10dg4eo6shzqd">
            <SendIcon className="h-4 w-4" data-id="element-153" data-mp="1k3tc6dflgt5z" />
          </button>
        </div>
      </form>
    </section>;
}